import React, { useState } from 'react';
import ReactSlider from 'react-slider';
import { formatNumbers } from '../../utils/convert-to-arabic-price';
import { useDebouncedCallback } from 'use-debounce';
import RIYALIcon from '../../assets/riyal.svg?react';

interface VerticalFiltersProps {
  filters: {
    price?: {
      min: number;
      max: number;
      value: { from: number; to: number };
    };
    categories?: {
      options: { value: string; count: number }[];
      value: string[];
    };
    brand?: {
      options: { value: string; count: number }[];
      value: string[];
    };
    color?: {
      options: { value: string; count: number }[];
      value: string[];
    };
    material?: {
      options: { value: string; count: number }[];
      value: string[];
    };
    size?: {
      options: { value: string; count: number }[];
      value: string[];
    };
    style?: {
      options: { value: string; count: number }[];
      value: string[];
    };
  };
  primaryColor: string;
  onFilterChange: (
    key: string,
    value: string[] | { from: number; to: number },
  ) => void;
  isMobileFiltersOpen: boolean;
  onCloseMobileFilters: () => void;
  currency: string;
  isDarkMode: boolean;
  language: string;
  availableFilters?: string[];
  locale?: string;
}

const VerticalFilters: React.FC<VerticalFiltersProps> = ({
  filters,
  primaryColor,
  onFilterChange,
  isMobileFiltersOpen,
  onCloseMobileFilters,
  currency,
  isDarkMode,
  language,
  availableFilters,
  locale = 'ar',
}) => {
  const [localPriceValue, setLocalPriceValue] = useState<{
    from: number;
    to: number;
  } | null>(null);

  const [showAllFilters, setShowAllFilters] = useState<{
    [key: string]: boolean;
  }>({});

  const debouncedPriceChange = useDebouncedCallback(
    (value: { from: number; to: number }) => {
      onFilterChange('price', value);
    },
    500,
  );

  const renderPriceFilter = () => {
    if (
      !filters.price ||
      filters.price.min === null ||
      filters.price.max === null
    )
      return null;

    const { min = 0, max = 1000, value } = filters.price;
    const currentValue = localPriceValue || {
      from: value.from ?? min,
      to: value.to ?? max,
    };

    const hasDecimals = (num: number) => num % 1 !== 0;
    const shouldUseDecimals = hasDecimals(min) || hasDecimals(max);
    const step = shouldUseDecimals ? 0.01 : 1;

    return (
      <div className="vertical-filter-section">
        <h3
          className="filter-title-vertical"
          style={{
            color: isMobileFiltersOpen ? '#000' : isDarkMode ? '#fff' : '#000',
          }}
        >
          {locale === 'ar' ? 'السعر' : 'Price'}
        </h3>
        <div className="price-slider">
          <ReactSlider
            className="horizontal-slider"
            thumbClassName="alfinder-thumb"
            trackClassName="track"
            renderTrack={(props, state) => {
              if (state.index === 1) {
                return (
                  <div
                    {...props}
                    style={{
                      ...props.style,
                      background: primaryColor || '#0056b3',
                    }}
                  />
                );
              }
              return <div {...props} />;
            }}
            min={min}
            max={max}
            step={step}
            value={[currentValue.from, currentValue.to]}
            invert
            onChange={(newRange) => {
              const newValue = shouldUseDecimals
                ? {
                    from: Number(newRange[0].toFixed(2)),
                    to: Number(newRange[1].toFixed(2)),
                  }
                : {
                    from: Math.round(newRange[0]),
                    to: Math.round(newRange[1]),
                  };
              setLocalPriceValue(newValue);
              debouncedPriceChange(newValue);
            }}
            renderThumb={(props, state) => (
              <div {...props}>
                <span
                  className="price-label"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    color: isMobileFiltersOpen
                      ? '#000'
                      : isDarkMode
                      ? '#fff'
                      : '#000',
                  }}
                >
                  {formatNumbers(
                    shouldUseDecimals
                      ? Number(state.valueNow.toFixed(2))
                      : Math.round(state.valueNow),
                    language as 'ar-SA' | 'en-US',
                    currency,
                  )}{' '}
                  {currency === 'SAR' && (
                    <RIYALIcon
                      fill={
                        isMobileFiltersOpen
                          ? '#000'
                          : isDarkMode
                          ? '#fff'
                          : '#000'
                      }
                    />
                  )}
                </span>
              </div>
            )}
          />
        </div>
      </div>
    );
  };

  // Generic function to render any checkbox filter
  const renderCheckboxFilter = (
    filterKey: string,
    filterData:
      | { options: { value: string; count: number }[]; value: string[] }
      | undefined,
    title: string,
    enTitle?: string,
    maxInitialItems: number = 6,
  ) => {
    if (!filterData) return null;

    const showAll = showAllFilters[filterKey] || false;
    const options = filterData.options;
    const displayOptions = showAll
      ? options
      : options.slice(0, maxInitialItems);
    const hasMoreOptions = options.length > maxInitialItems;

    return (
      <div className="vertical-filter-section">
        <h3
          className="filter-title"
          style={{
            color: isMobileFiltersOpen ? '#000' : isDarkMode ? '#fff' : '#000',
          }}
        >
          {locale === 'ar' ? title : enTitle || title}
        </h3>
        <div className="categories-list">
          {displayOptions.map((option) => (
            <label key={option.value} className="category-checkbox">
              <input
                type="checkbox"
                style={
                  {
                    '--checkbox-color': primaryColor,
                  } as React.CSSProperties
                }
                checked={filterData?.value.includes(option.value)}
                onChange={() => {
                  const currentValues = filterData?.value || [];
                  const updatedValues = currentValues.includes(option.value)
                    ? currentValues.filter((v) => v !== option.value)
                    : [...currentValues, option.value];
                  onFilterChange(filterKey, updatedValues);
                }}
              />
              <span
                className="category-label"
                style={{
                  color: isMobileFiltersOpen
                    ? '#000'
                    : isDarkMode
                    ? '#fff'
                    : '#000',
                }}
              >
                {option.value}
              </span>
              {/* <span className="category-count">({option.count})</span> */}
            </label>
          ))}
          {hasMoreOptions && (
            <button
              className="show-more-button"
              onClick={() =>
                setShowAllFilters((prev) => ({
                  ...prev,
                  [filterKey]: !showAll,
                }))
              }
              style={{ color: primaryColor }}
            >
              {showAll ? 'عرض أقل' : 'عرض المزيد'}
            </button>
          )}
        </div>
      </div>
    );
  };

  // Configuration for all possible checkbox filters
  const allFilterConfigs = [
    {
      key: 'categories',
      dataKey: 'categories' as keyof typeof filters,
      title: 'الفئات',
      enTitle: 'Categories',
    },
    {
      key: 'brand',
      dataKey: 'brand' as keyof typeof filters,
      title: 'العلامات التجارية',
      enTitle: 'Brand',
    },
    {
      key: 'color',
      dataKey: 'color' as keyof typeof filters,
      title: 'الألوان',
      enTitle: 'Color',
    },
    {
      key: 'material',
      dataKey: 'material' as keyof typeof filters,
      title: 'المواد',
      enTitle: 'Material',
    },
    {
      key: 'size',
      dataKey: 'size' as keyof typeof filters,
      title: window.location.hostname === 'laverne.com' ? 'الحجم' : 'المقاس',
      enTitle: 'Size',
    },
    {
      key: 'style',
      dataKey: 'style' as keyof typeof filters,
      title: 'النمط',
      enTitle: 'Style',
    },
  ];

  // Filter and sort based on availableFilters from API
  const filterConfigs = availableFilters
    ? availableFilters
        .map((key) => allFilterConfigs.find((config) => config.key === key))
        .filter((config): config is NonNullable<typeof config> => !!config)
    : allFilterConfigs;

  const renderAllCheckboxFilters = () => {
    return filterConfigs
      .map(({ key, dataKey, title, enTitle }) => {
        const filterData = filters[dataKey] as
          | { options: { value: string; count: number }[]; value: string[] }
          | undefined;
        // Only render if filter has data
        if (!filterData) return null;
        return renderCheckboxFilter(key, filterData, title, enTitle);
      })
      .filter(Boolean); // Remove null entries
  };

  return (
    <>
      <div
        className={`vertical-filters-overlay ${
          isMobileFiltersOpen ? 'active' : ''
        }`}
        onClick={onCloseMobileFilters}
      />
      <div
        className={`vertical-filters ${
          isMobileFiltersOpen ? 'mobile-open' : ''
        }`}
      >
        {isMobileFiltersOpen && (
          <div className="mobile-filters-header">
            <h2>{locale === 'ar' ? 'تصفية المنتجات' : 'Filter Products'}</h2>
            <button
              onClick={onCloseMobileFilters}
              className="close-filters-button"
            >
              <i className="sicon-close"></i>
            </button>
          </div>
        )}
        {renderPriceFilter()}
        {renderAllCheckboxFilters()}
      </div>
    </>
  );
};

export default VerticalFilters;
